import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { generateThumbnail } from './thumbnailService';

const PROJECTS_COLLECTION = 'projects';
const STORAGE_PATH = 'projects';

export const projectService = {
  // Create a new project
  async createProject(projectData) {
    try {
      const docRef = await addDoc(collection(db, PROJECTS_COLLECTION), {
        ...projectData,
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  // Create a project with a specific ID (for migration)
  async createProjectWithId(projectId, projectData) {
    try {
      const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
      await setDoc(projectRef, {
        ...projectData,
        updatedAt: new Date()
      });
      return projectId;
    } catch (error) {
      console.error('Error creating project with ID:', error);
      throw error;
    }
  },

  // Get all projects (including drafts - for admin use)
  async getAllProjects() {
    try {
      const querySnapshot = await getDocs(collection(db, PROJECTS_COLLECTION));
      const projects = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by draft status (published first), then by featured, then by project date
      return projects.sort((a, b) => {
        // Drafts go to the bottom
        if (!a.isDraft && b.isDraft) return -1;
        if (a.isDraft && !b.isDraft) return 1;

        // If both are drafts or both are published, sort by featured first
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;

        // If same featured status, sort by project date (newest first)
        const aDate = a.projectDate ? new Date(a.projectDate) : new Date(0);
        const bDate = b.projectDate ? new Date(b.projectDate) : new Date(0);
        return bDate - aDate;
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  // Get a single project by id. Used for ProjectDetail so it does not have
  // to fetch the entire collection just to .find() one document.
  async getProjectById(projectId) {
    try {
      const snapshot = await getDoc(doc(db, PROJECTS_COLLECTION, projectId));
      if (!snapshot.exists()) return null;
      const data = { id: snapshot.id, ...snapshot.data() };
      // Don't leak drafts to the public view.
      if (data.isDraft) return null;
      return data;
    } catch (error) {
      console.error('Error fetching project by id:', error);
      throw error;
    }
  },

  // Get published projects only (excluding drafts - for public use)
  async getPublishedProjects() {
    try {
      const querySnapshot = await getDocs(collection(db, PROJECTS_COLLECTION));
      const projects = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter out drafts
      const publishedProjects = projects.filter(project => !project.isDraft);

      // Sort by featured projects first, then by project date (newest first)
      return publishedProjects.sort((a, b) => {
        // Featured projects come first
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;

        // If both featured or both not featured, sort by project date (newest first)
        const aDate = a.projectDate ? new Date(a.projectDate) : new Date(0);
        const bDate = b.projectDate ? new Date(b.projectDate) : new Date(0);
        return bDate - aDate;
      });
    } catch (error) {
      console.error('Error fetching published projects:', error);
      throw error;
    }
  },

  // Update a project
  async updateProject(projectId, updates) {
    try {
      const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
      await updateDoc(projectRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  // Delete a project
  async deleteProject(projectId) {
    try {
      await deleteDoc(doc(db, PROJECTS_COLLECTION, projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  // Upload image to Firebase Storage
  async uploadImage(file, projectId, fileName) {
    try {
      const storageRef = ref(storage, `${STORAGE_PATH}/${projectId}/images/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  // Upload thumbnail to Firebase Storage
  async uploadThumbnail(blob, projectId, fileName) {
    try {
      const thumbFileName = fileName.replace(/\.[^.]+$/, '_thumb.jpg');
      const storageRef = ref(storage, `${STORAGE_PATH}/${projectId}/thumbnails/${thumbFileName}`);
      const snapshot = await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      throw error;
    }
  },

  // Upload image with thumbnail generation
  async uploadImageWithThumbnail(file, projectId, fileName) {
    try {
      // Upload original image
      const imageUrl = await this.uploadImage(file, projectId, fileName);

      // Generate and upload thumbnail
      const thumbnailBlob = await generateThumbnail(file);
      const thumbnailUrl = await this.uploadThumbnail(thumbnailBlob, projectId, fileName);

      return { imageUrl, thumbnailUrl };
    } catch (error) {
      console.error('Error uploading image with thumbnail:', error);
      throw error;
    }
  },

  // Upload video to Firebase Storage
  async uploadVideo(file, projectId, fileName) {
    try {
      const storageRef = ref(storage, `${STORAGE_PATH}/${projectId}/videos/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  },

  // Delete file from Firebase Storage
  async deleteFile(filePath) {
    try {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },

  // Batch upload multiple files
  async batchUploadFiles(files, projectId, type = 'images') {
    try {
      const uploadPromises = files.map(async (file, index) => {
        const fileName = `${type}_${index}_${Date.now()}.${file.name.split('.').pop()}`;
        if (type === 'images') {
          return await this.uploadImage(file, projectId, fileName);
        } else {
          return await this.uploadVideo(file, projectId, fileName);
        }
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error batch uploading files:', error);
      throw error;
    }
  },

  // Batch upload images with thumbnails
  async batchUploadImagesWithThumbnails(files, projectId) {
    try {
      const uploadPromises = files.map(async (file, index) => {
        const fileName = `images_${index}_${Date.now()}.${file.name.split('.').pop()}`;
        return await this.uploadImageWithThumbnail(file, projectId, fileName);
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error batch uploading images with thumbnails:', error);
      throw error;
    }
  },

  // Extract storage path from Firebase URL
  getStoragePathFromUrl(url) {
    try {
      if (!url) return null;
      
      // Firebase Storage URLs have the format:
      // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?{params}
      const urlObj = new URL(url);
      const pathParam = urlObj.searchParams.get('name') || urlObj.pathname.split('/o/')[1]?.split('?')[0];
      
      if (pathParam) {
        return decodeURIComponent(pathParam);
      }
      
      // Fallback: try to extract from pathname
      const pathParts = urlObj.pathname.split('/o/');
      if (pathParts.length > 1) {
        return decodeURIComponent(pathParts[1].split('?')[0]);
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting storage path from URL:', error);
      return null;
    }
  },

  // Delete file by URL
  async deleteFileByUrl(url) {
    try {
      const storagePath = this.getStoragePathFromUrl(url);
      if (storagePath) {
        await this.deleteFile(storagePath);
      }
    } catch (error) {
      console.error('Error deleting file by URL:', error);
      // Don't throw error to avoid blocking the main operation
    }
  }
};